import { ChangeEvent, useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import type { Governance } from "../../GovernanceV2/types/Governance";
import { DASH, formatHours, formatMoney } from "./format";
import type { PreviewGovernance } from "./types";

export interface ChangeOrderGovernanceProps {
  governances: Governance[];
  preview: PreviewGovernance[] | null;
  overrides: Record<string, number>;
  onOverride: (id: string, hours: number | null) => void;
  currency: string;
}

const isNegative = (text: string) => text.trim() !== "" && Number(text) < 0;

interface FixedHoursInputProps {
  id: string;
  description: string;
  setHours: number;
  minimumHours: number;
  override?: number;
  onOverride: (id: string, hours: number | null) => void;
}

// Keeps the typed text locally so the field can be cleared and retyped.
const FixedHoursInput = ({
  id,
  description,
  setHours,
  minimumHours,
  override,
  onOverride,
}: FixedHoursInputProps) => {
  const [text, setText] = useState(String(override ?? setHours));

  // Resync after an external change (e.g. a reset) that the text doesn't explain.
  useEffect(() => {
    setText((current) => {
      const n = Number(current);
      const blank = current.trim() === "" || isNaN(n);
      if (override !== undefined) {
        return !blank && n === override ? current : String(override);
      }
      return blank || n < 0 || n === setHours ? current : String(setHours);
    });
  }, [override, setHours]);

  const invalid = isNegative(text);
  return (
    <>
      <Form.Control
        type="number"
        size="sm"
        min={0}
        step="any"
        aria-label={`Hours for ${description}`}
        className={override !== undefined && !invalid ? "customItem" : undefined}
        isInvalid={invalid}
        value={text}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          const n = Number(v);
          setText(v);
          onOverride(
            id,
            v.trim() === "" || isNaN(n) || n < 0 || n === setHours ? null : n
          );
        }}
        onBlur={() => {
          if (override === undefined) setText(String(setHours));
        }}
      />
      {invalid && (
        <Form.Control.Feedback type="invalid">
          Enter zero or more
        </Form.Control.Feedback>
      )}
      {minimumHours > 0 && (
        <Form.Text className="text-muted">
          Minimum {formatHours(minimumHours)} (0 leaves it out)
        </Form.Text>
      )}
    </>
  );
};

// Governance on this change request alone, calculated by the preview. Fixed
// rows can be set here; percent rows follow the change's hours.
const ChangeOrderGovernance = ({
  governances,
  preview,
  overrides,
  onOverride,
  currency,
}: ChangeOrderGovernanceProps) => {
  if (governances.length === 0) return null;
  const previewFor = (id: string) =>
    preview?.find((g) => g["source-id"] === String(id));

  return (
    <div className="mb-4">
      <h6 className="mb-0">Project governance</h6>
      <Form.Text className="text-muted mb-2 d-block">
        Calculated on this change request&apos;s hours
      </Form.Text>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Governance</th>
            <th>Calculated by</th>
            <th>Hours</th>
            <th>Price</th>
          </tr>
        </thead>
        <tbody>
          {governances.map((g) => {
            const id = String(g.id);
            const p = previewFor(id);
            const fixed = g.calculationType === "fixed_hours";
            const setHours = Number(g.hours) || 0;
            return (
              <tr key={id}>
                <td>{g.description}</td>
                <td>{g.calculationTypeForDisplay as string}</td>
                <td>
                  {fixed ? (
                    <FixedHoursInput
                      id={id}
                      description={g.description}
                      setHours={setHours}
                      minimumHours={Number(g.minimumHours) || 0}
                      override={overrides[id]}
                      onOverride={onOverride}
                    />
                  ) : p ? (
                    formatHours(p.hours)
                  ) : (
                    DASH
                  )}
                </td>
                <td>{p ? formatMoney(Number(p.revenue), currency) : DASH}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ChangeOrderGovernance;
